/**
 * The open generator sheets (W3-05; UX-038, DESIGN.md §4.14 "one generator sheet at a time").
 *
 * Every `ToolSheet` registers its Cancel while it is mounted. The batch module uses the list to
 *  - close the open sheet (reverting its live preview synchronously) before another tool sheet opens, so sheets
 *    never stack and a new sheet computes from the committed document, not from another sheet's ghosts;
 *  - answer the `batch.sheetOpen` condition of the global Escape binding (`tools.closeSheet`): Escape closes the
 *    sheet from anywhere, unless something more specific uses it (a hold or segment being placed, the paste ghost,
 *    a drag, a text field, a focused dialog).
 */

import { DIALOG, type DialogId, type DialogMap } from '../../app/dialogs/types';
import type { AppServices } from '../../app/registry/types';

interface Entry {
  readonly token: number;
  readonly cancel: () => void;
}

const open: Entry[] = [];
let nextToken = 1;

/** Registers a mounted sheet's Cancel; returns the disposer (call it on unmount). */
export function registerSheet(cancel: () => void): () => void {
  const entry: Entry = { token: nextToken++, cancel };
  open.push(entry);
  return () => {
    const i = open.indexOf(entry);
    if (i >= 0) open.splice(i, 1);
  };
}

/** The motion templates sheet (easing module) marks its body with this attribute. */
const TEMPLATES_SHEET = '[data-templates-sheet]';

function templatesSheetShown(): boolean {
  return typeof document !== 'undefined' && document.querySelector(TEMPLATES_SHEET) !== null;
}

/** True while a generator sheet (or the motion templates sheet) is open. */
export function sheetOpen(): boolean {
  return open.length > 0 || templatesSheetShown();
}

/**
 * Closes the most recently opened sheet (Escape): a generator sheet is cancelled (its preview reverts), the
 * templates sheet is closed through the dialog host (it reverts its own preview when it unmounts). False when none
 * is open.
 */
export function cancelTopSheet(ui?: Pick<AppServices['ui'], 'closeDialog'>): boolean {
  const top = open[open.length - 1];
  if (top !== undefined) {
    top.cancel();
    return true;
  }
  if (ui !== undefined && templatesSheetShown()) {
    ui.closeDialog(DIALOG.templates);
    return true;
  }
  return false;
}

/** Cancels every open sheet (their previews are reverted and they close). */
export function cancelOpenSheets(): void {
  for (const e of [...open].reverse()) e.cancel();
}

/** Tool sheets and the motion templates sheet: at most one of them is open. */
export const TOOL_SHEETS = [
  DIALOG.distribute,
  DIALOG.clone,
  DIALOG.fill,
  DIALOG.motionPath,
  DIALOG.bake,
  DIALOG.flatten,
  DIALOG.templates,
] as const satisfies readonly DialogId[];

export type ToolSheetId = (typeof TOOL_SHEETS)[number];

/**
 * Opens a tool sheet after closing whatever sheet is open (UX-038). Registered sheets are cancelled synchronously
 * (their previews revert before the new sheet reads the document); the others (the templates sheet of the easing
 * module) are closed through the dialog host.
 */
export async function openToolSheet<K extends ToolSheetId>(
  services: Pick<AppServices, 'ui'>,
  id: K,
  props: DialogMap[K]['props'],
): Promise<DialogMap[K]['result']> {
  cancelOpenSheets();
  for (const other of TOOL_SHEETS) if (other !== id) services.ui.closeDialog(other);
  return services.ui.openDialog(id, props);
}

/** Test hook: forget every registration. */
export function resetSheetRegistry(): void {
  open.length = 0;
}
